import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { isEmpty, pick, prop } from 'ramda';

import { NavigationArrows } from './components/NavigationArrows';

import { Logo } from 'js/app/components/common/Logo';
import { SvgIcon } from 'js/app/components/common/SvgIcon';
import { WebContentWithFormFactor } from 'js/app/components/common/WebContentWithFormFactor';
import { DetailedTile } from 'js/app/components/main/Games/components/GameTile/components/DetailedTile';
import { SlotPage } from 'js/app/components/main/SlotPage';
import { Button } from 'js/components/common/Button';
import { ManagedPopup } from 'js/components/common/Popup';
import { useOnClose } from 'js/components/common/PopupManager';

import { useAutoGameLaunch } from 'js/app/components/main/Games/hooks/useGameLaunch';
import { useInDesktop, useInMobile } from 'js/app/hooks/device/hooks/useMediaQuery';
import { clsx } from 'js/app/utils/clsx.util';
import { useNavigateToPopupBackgroundPage } from 'js/components/system/Router/components/PageRouter/hooks';
import { useTranslations } from 'js/hooks/i18n';
import { useAppSelector, usePageSelector } from 'js/store/hooks';

import { GAMES_INFO_POPUP_ID, SLOT_PAGE_ASSET_NAME } from './constants';
import { getGameFeedFromStorage, removeGameFeedFromStorage, removeGameListFromStorage } from './utils';

import style from './GamesInfoPopup.scss';

export const GamesInfoPopup = ({ onClose: navigateOnClose, gameCode, isLogoHidden }) => {
    const isMobile = useInMobile();
    const path = usePageSelector(prop('path'));
    const isDesktop = useInDesktop();
    const onClose = useOnClose(GAMES_INFO_POPUP_ID);
    const { gamesConfiguration, rootPage } = useAppSelector(pick(['gamesConfiguration', 'rootPage']));
    const [showArrows, setShowArrows] = useState(true);
    const [customCloseClass, setCustomCloseClass] = useState('');
    const navigateToPopupBackgroundPage = useNavigateToPopupBackgroundPage();
    const translate = useTranslations('playtech.mobile.system.buzzGame.infoPopup.');
    const feedName = getGameFeedFromStorage();

    const gameData = useMemo(() => Object.values(gamesConfiguration)
        .find(({ seoFriendlyGameName, gameCode: code, mobileGameCode }) =>
            seoFriendlyGameName === gameCode
            || code === gameCode
            || mobileGameCode === gameCode
        ) || {},
    [gameCode, gamesConfiguration]
    );

    const closeHandler = useCallback(({ isOnlyClose, isOnClose } = {}) => {
        if (isOnClose) {
            return;
        }

        removeGameListFromStorage();
        removeGameFeedFromStorage();
        onClose();

        if (isOnlyClose) {
            navigateOnClose();
            return;
        }

        path === rootPage ? navigateOnClose() : navigateToPopupBackgroundPage();
    }, [navigateOnClose, navigateToPopupBackgroundPage, onClose, path, rootPage]);

    const customSlotPageArticleId = useMemo(
        () => gameData.additionalAssets?.find(asset => asset.name === SLOT_PAGE_ASSET_NAME)?.url,
        [gameData.additionalAssets]
    );

    const popupClassNames = useMemo(() => customSlotPageArticleId
        ? {
            close: clsx(style.popupClose, customCloseClass),
            inner: style.slotInner
        }
        : { inner: style.inner },
    [customCloseClass, customSlotPageArticleId]
    );

    const launchGame = useAutoGameLaunch({ ...gameData, cb: closeHandler, feedName });

    const ButtonLaunchGame = () => (
        <Button
            size="large"
            className={style.cta}
            onClick={launchGame}
        >
            {translate('cta')}
        </Button>
    );

    if (isEmpty(gameData)) {
        closeHandler();
        return null;
    }

    return (
        <ManagedPopup
            fullscreen
            onClose={closeHandler}
            classNames={popupClassNames}
            layerClassNames={{ layout: style.layout }}
            className={clsx(style.container, { [style.containerForSlot]: customSlotPageArticleId })}
        >
            {showArrows && (
                <NavigationArrows gameCode={gameCode} />
            )}

            {customSlotPageArticleId
                ? (
                    <>
                        <Suspense fallback="">
                            <SlotPage
                                isInPopup
                                className={style.slotPage}
                                gameCode={gameData.gameCode}
                                setShowArrows={setShowArrows}
                                webContentId={customSlotPageArticleId}
                                closeHandler={closeHandler}
                                setCustomCloseClass={setCustomCloseClass}
                            />
                        </Suspense>

                        {!isMobile && (
                            <SvgIcon
                                type="close-popup"
                                className={style.customPopupClose}
                                onClick={closeHandler}
                            />
                        )}
                    </>
                )
                : (
                    <>
                        <div className={style.wrapper}>
                            {!isLogoHidden && isMobile && (
                                <Logo
                                    href={rootPage}
                                    className={style.logo}
                                    size="4rem"
                                />
                            )}

                            <div className={style.gameImageWrapper}>
                                <DetailedTile
                                    viewModeInline={false}
                                    gameData={gameData}
                                    disableHoverEffect
                                    cb={closeHandler}
                                />
                            </div>

                            <div className={style.webContentWrapper}>
                                <div className={style.gameTitle}>{gameData.name}</div>

                                <Suspense fallback="">
                                    <WebContentWithFormFactor
                                        id={gameData.articleId}
                                        mobileId={gameData.mobileArticleId}
                                        initContent={' '}
                                        fallbackContent={translate('emptyContent')}
                                        className={style.webContent}
                                    />
                                </Suspense>

                                {!isDesktop && <ButtonLaunchGame />}
                            </div>
                        </div>

                        {isDesktop && <ButtonLaunchGame />}
                    </>
                )
            }
        </ManagedPopup>
    );
};
